import { queryOptions, type Query, type QueryClient } from "@tanstack/react-query";
import type { RequestWorkflowService } from "@/services/request-workflow.service";
import type {
  TRequestWorkflow,
  TRequestWorkflowPendingTransition,
  TRequestWorkflowTransition,
  TRequestWorkflowTransitionActivity,
} from "@/types/request-workflow";
import { createBatcher, type Batcher } from "./batch";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * One intake form's legacy workflow rules (NG-2809): for each request status,
 * who may move a request out of it, to where, and whose approval that needs.
 * The status select on every requests-list row, board card and request header
 * reads it to offer only the moves the caller may make; the form's Workflow
 * settings tab edits it.
 *
 * - **Batched.** Each form has its own entry, but the read goes through
 *   `{ws}/intake/workflow-states/?form_ids=`: every form asked for in one tick
 *   (a page of list rows) shares a request, up to the endpoint's 100
 *   (`WORKFLOW_STATE_BULK_FORM_LIMIT`). One GET per row was the N+1 Sentry
 *   flagged on the /requests navigation (NEXT-GEN-FRONTEND-2B).
 * - **Reference.** Rules are admin configuration, so an answer is reused for
 *   5 min and never re-read on a timer. Approval state is not in them: a
 *   request's pending approval rides on the request row
 *   (`pending_transitions`), which the requests reads keep fresh.
 *
 * @writers The rule writers in `RequestWorkflowStore` (state update, reset,
 * transition create/update/delete, movers, approvers) re-read the form's entry
 * and its activity after a confirmed write (`invalidateCachedRequestWorkflow`).
 */

/** The batch endpoint's own limit (`WORKFLOW_STATE_BULK_FORM_LIMIT`). */
export const MAX_FORMS_PER_REQUEST = 100;

const DOMAIN = "request-workflows";

/** `[slug, uid, 'request-workflows', formId]`. */
export function requestWorkflowKey(scope: QueryScope, formId: string) {
  return scopedKey(scope, DOMAIN, formId);
}

interface RulesRead {
  readonly tenant: string;
  readonly formId: string;
}

type RulesService = Pick<RequestWorkflowService, "listWorkflowStatesForForms">;

/** One batcher per service, so every reader of one app shares its ticks. */
const batchers = new WeakMap<RulesService, Batcher<RulesRead, TRequestWorkflow[]>>();

function batcherFor(service: RulesService) {
  let batcher = batchers.get(service);
  if (!batcher) {
    batcher = createBatcher<RulesRead, TRequestWorkflow[]>({
      name: DOMAIN,
      idOf: (read) => read.formId,
      // Another workspace's forms go in their own request.
      groupOf: (read) => read.tenant,
      maxBatch: MAX_FORMS_PER_REQUEST,
      fetchBatch: async (reads, request) => {
        const formIds = reads.map((read) => read.formId);
        const byForm = await service.listWorkflowStatesForForms(reads[0].tenant, formIds, request);
        // A form the answer leaves out was soft-deleted while its requests kept
        // listing. It has no rules, so the select offers every status, as the
        // store did; rejecting it would surface as an unhandled 404 per row
        // (Sentry NEXT-GEN-FRONTEND-1K).
        return new Map(formIds.map((formId) => [formId, byForm[formId] ?? []]));
      },
    });
    batchers.set(service, batcher);
  }
  return batcher;
}

export function requestWorkflowQuery(service: RulesService, scope: QueryScope, formId: string) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: requestWorkflowKey(scope, formId),
    queryFn: scopedRead(
      (request) => batcherFor(service).load({ tenant: scope.tenant, formId }, request),
      { retry: "safe-read" },
    ),
  });
}

/** `[slug, uid, 'request-workflows', formId, 'activity']`: under the rules, so a rule write re-reads it. */
function requestWorkflowActivityKey(scope: QueryScope, formId: string) {
  return scopedKey(scope, DOMAIN, formId, "activity");
}

/**
 * One form's workflow history, `GET intake/forms/{id}/workflow-activity/`: who
 * requested, approved or rejected which transition (the Workflow tab's log).
 * Mutable: approvals land while the tab is open.
 *
 * @writers The same as `requestWorkflowQuery`; the approval writers re-read it too.
 */
export function requestWorkflowActivityQuery(
  service: Pick<RequestWorkflowService, "listActivity">,
  scope: QueryScope,
  formId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: requestWorkflowActivityKey(scope, formId),
    queryFn: scopedRead(
      (request): Promise<TRequestWorkflowTransitionActivity[]> =>
        service.listActivity(scope.tenant, formId, undefined, request),
      { retry: "safe-read" },
    ),
  });
}

/** A rules or activity entry of `formId` in `tenant`, under any principal. */
function isFormWorkflowQuery(tenant: string, formId: string, query: Query): boolean {
  const key = query.queryKey;
  return key[0] === tenant && key[2] === DOMAIN && key[3] === formId;
}

/** After a rule write: re-read the form's rules and its activity. */
export function invalidateCachedRequestWorkflow(
  client: QueryClient,
  tenant: string,
  formId: string,
): Promise<void> {
  return client.invalidateQueries({
    predicate: (query) => isFormWorkflowQuery(tenant, formId, query),
  });
}

/** After an approval vote or request: re-read the form's activity only. */
export function invalidateCachedRequestWorkflowActivity(
  client: QueryClient,
  tenant: string,
  formId: string,
): Promise<void> {
  return client.invalidateQueries({
    predicate: (query) =>
      isFormWorkflowQuery(tenant, formId, query) && query.queryKey[4] === "activity",
  });
}

/** A request's pending transition, paired with the rule it asks to take. */
export interface PendingTransitionDetails {
  readonly workflow: TRequestWorkflow;
  readonly transition: TRequestWorkflowTransition;
  readonly pending: TRequestWorkflowPendingTransition;
}

/**
 * The transition awaiting approval on a request, paired with its rule (for the
 * approvers and the threshold). Pending is per request, so another request's
 * approval never surfaces here.
 */
export function pendingTransitionDetails(
  pending: readonly TRequestWorkflowPendingTransition[],
  workflows: readonly TRequestWorkflow[],
): PendingTransitionDetails | undefined {
  const first = pending[0];
  if (!first) return undefined;
  for (const workflow of workflows) {
    const transition = workflow.transitions.find((t) => t.id === first.workflow_transition_id);
    if (transition) return { workflow, transition, pending: first };
  }
  return undefined;
}
